import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

const SeaFreightCertificatePrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (companyData) { setCompany(companyData); setLoading(false); return; }
    (async () => {
      try {
        const dr = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuth() });
        if (dr.ok) {
          const d = await dr.json();
          const cr = await fetch(`${backendUrl}/api/companies/${d.company_id}`, { headers: getAuth() });
          if (cr.ok) setCompany(await cr.json());
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [docketId, companyData]);

  useImperativeHandle(ref, () => ({ print: () => window.print() }));

  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };
  const fmtPair = (val, date) => [val, fmt(date)].filter(Boolean).join(' / ');

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const companyName    = company?.name        || 'COMPANY NAME';
  const companyLogo    = company?.logo_url;
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone   = company?.phone       || '';
  const companyEmail   = company?.email       || '';
  const companyWebsite = company?.website     || '';
  const companyGst     = company?.gst_number  || '';
  const companyCin     = company?.cin_number  || '';

  const containers = Array.isArray(formData.containers) ? formData.containers : [];

  // Shipment detail rows — SEA labels
  const shipmentRows = [
    { label: 'Shipping Line',              value: formData.shipping_line || '' },
    { label: 'Vessel / Voyage / Date',     value: fmtPair(formData.vessel_voyage, formData.vessel_date) },
    { label: 'Port of Load',               value: formData.port_of_load || '' },
    { label: 'Port of Discharge',          value: formData.port_of_discharge || '' },
    { label: 'Final Place of Destination', value: formData.final_destination || '' },
    { label: 'MBL No. / Date',             value: fmtPair(formData.mbl_no, formData.mbl_date) },
    { label: 'CBM',                        value: formData.cbm || '' },
    { label: 'HBL No. / Date',             value: fmtPair(formData.hbl_no, formData.hbl_date) },
    { label: 'No. of Packages HBL',        value: formData.no_of_packages_hbl || '' },
    { label: 'Gr.wt. HBL',                value: formData.gross_weight_hbl || '' },
    { label: 'Description HBL',            value: formData.hbl_description || '' },
    { label: 'IGM No. / Date',             value: fmtPair(formData.igm_no, formData.igm_date) },
    { label: 'Temperature',                value: formData.temperature || '' },
  ];

  return (
    <>
      <div
        id="sea-fc-print-content"
        className="bg-white print:bg-white"
        style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4' }}
      >
        <div className="p-6 print:p-6 relative">

          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
            <div className="font-bold text-center whitespace-nowrap transform -rotate-45"
              style={{ fontSize: '55px', opacity: 0.05, letterSpacing: '8px', color: '#555' }}>
              {companyName}
            </div>
          </div>

          <div className="relative" style={{ zIndex: 1 }}>

            {/* ── Header ── */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-shrink-0">
                {companyLogo
                  ? <img src={companyLogo} alt={companyName} className="h-16 w-auto object-contain" />
                  : <div className="h-16 w-20 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">LOGO</div>}
              </div>
              <div className="text-right text-xs">
                <p className="font-semibold">Freight Forwarder</p>
                <p className="font-semibold">Custom Broker</p>
                <p className="font-semibold">Reefer Transportation</p>
                <p className="font-semibold">Logistics Solution</p>
                {companyWebsite && <p className="mt-1 text-blue-600">{companyWebsite}</p>}
              </div>
            </div>

            <div className="border-t-2 border-black mb-3"></div>

            {/* ── Title ── */}
            <div className="text-center mb-2">
              <h1 className="text-base font-bold">SEA FREIGHT CERTIFICATE</h1>
            </div>

            {/* ── Job No / Date ── */}
            <div className="flex justify-between mb-3 text-xs">
              <div><span className="font-semibold">Job No.: </span>{formData.job_no || ''}</div>
              <div><span className="font-semibold">Date: </span>{fmt(formData.date)}</div>
            </div>

            {/* ── Shipper / Consignee table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <thead>
                <tr>
                  <th className="border border-black p-2 text-left font-bold w-1/3 bg-gray-50">SHIPPER</th>
                  <th className="border border-black p-2 text-left font-bold w-1/3 bg-gray-50">CONSIGNEE</th>
                  <th className="border border-black p-2 text-left font-bold w-1/3 bg-gray-50">NOTIFY</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black p-2 align-top">
                    <div className="whitespace-pre-wrap text-[10px] min-h-[50px]">{formData.shipper || ''}</div>
                  </td>
                  <td className="border border-black p-2 align-top">
                    <div className="whitespace-pre-wrap text-[10px] min-h-[50px]">{formData.consignee || ''}</div>
                  </td>
                  <td className="border border-black p-2 align-top">
                    <div className="whitespace-pre-wrap text-[10px] min-h-[50px]">{formData.notify || ''}</div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── Shipment Details table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <tbody>
                {shipmentRows.map((row) => (
                  <tr key={row.label}>
                    <td className="border border-black p-1.5 font-semibold bg-gray-50" style={{ width: '35%', whiteSpace: 'nowrap' }}>
                      {row.label}
                    </td>
                    <td className="border border-black p-1.5">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Container table (if any) ── */}
            {containers.filter(c => c.container_no || c.size).length > 0 && (
              <table className="w-full border-collapse border border-black text-xs mb-3">
                <thead>
                  <tr>
                    <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Container No.</th>
                    <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {containers.map((c, i) => (
                    <tr key={i}>
                      <td className="border border-black p-1.5">{c.container_no || ''}</td>
                      <td className="border border-black p-1.5">{c.size || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Freight Details table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-2">
              <thead>
                <tr>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50" style={{ width: '40%' }}>Description</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Qty</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Currency</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Amount</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black p-1.5 align-top">
                    <div className="whitespace-pre-wrap min-h-[40px]">{formData.description || ''}</div>
                  </td>
                  <td className="border border-black p-1.5 align-top">{formData.quantity || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.currency || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.amount || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.total_amount || ''}</td>
                </tr>
              </tbody>
            </table>

            {/* ── Note ── */}
            <div className="mb-3 text-[9px] text-gray-700">
              <span className="font-semibold">Note : </span>
              Subject to GST, Freight certificate is not the Invoice. Invoice will be issued separately after completion of the Shipment.
            </div>

            {/* ── Signature ── */}
            <div className="flex justify-end mb-4 mt-4">
              <div className="text-right text-xs">
                <p className="font-semibold mb-8">For, {companyName}</p>
                <div className="border-t border-black pt-1 w-44 text-center">Authorized Signatory</div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div id="sea-fc-footer" className="mt-4">
              <div className="border-t-2 border-black mb-2"></div>
              <div className="text-center text-[9px] text-gray-800">
                <p className="font-bold">{companyName}</p>
                <p>
                  {companyAddress && <span>Reg Off: {companyAddress}</span>}
                  {companyAddress && companyPhone && <span>. </span>}
                  {companyPhone && <span>Tel: {companyPhone}</span>}
                  {companyPhone && companyGst && <span> </span>}
                  {companyGst && <span>GST No.: {companyGst}</span>}
                  {companyGst && companyEmail && <span> </span>}
                  {companyEmail && <span>Email: {companyEmail}</span>}
                  {companyEmail && companyCin && <span> </span>}
                  {companyCin && <span>LLP IN No.: {companyCin}</span>}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; height: auto !important; min-height: 0 !important; }
          body * { visibility: hidden; }
          #sea-fc-print-content, #sea-fc-print-content * { visibility: visible; }
          #sea-fc-print-content {
            position: absolute; left: 0; top: 0; width: 100%;
            min-height: calc(297mm - 20mm);
            display: flex !important; flex-direction: column !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          #sea-fc-print-content > div { flex: 1 !important; display: flex !important; flex-direction: column !important; }
          #sea-fc-print-content > div > div.relative { flex: 1 !important; display: flex !important; flex-direction: column !important; }
          #sea-fc-footer { margin-top: auto !important; }
          table, td, th { border-color: black !important; }
          .bg-gray-50 { background-color: #f9fafb !important; -webkit-print-color-adjust: exact !important; }
        }
        @media screen {
          #sea-fc-print-content { max-width: 210mm; margin: 0 auto; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
        }
      `}</style>
    </>
  );
});

SeaFreightCertificatePrint.displayName = 'SeaFreightCertificatePrint';
export default SeaFreightCertificatePrint;
